/**
 * Widget de ENSAL · patrón "façade".
 *
 * PRESUPUESTO DE RENDIMIENTO (acordado con ENSAL, no negociable):
 *   · 0 KB de JS bloqueante            · 0 peticiones antes del primer clic
 *   · ≤ 18 KB gzip en la burbuja       · CLS +0.000  · INP +30 ms como techo
 *
 * Lo que se carga de entrada es SOLO este archivo: pinta una burbuja estática
 * y espera. El motor de chat (streaming, markdown, estado) vive en un chunk
 * aparte que se descarga con import() dinámico en el primer clic. Un usuario
 * que no abre el chat no paga nada más que estos bytes.
 *
 * Por qué Shadow DOM: Elementor inyecta CSS muy agresivo. Encapsular evita que
 * Elementor rompa el widget y, sobre todo, que el widget rompa a Elementor.
 *
 * Por qué position:fixed desde el primer frame: el widget nunca entra en el
 * flujo del documento, así que no puede empujar contenido. CLS estructuralmente
 * imposible, no "cuidado con el CLS".
 */

/**
 * A qué servidor habla el widget.
 *
 * Sale de la URL de este mismo módulo, no de un atributo del <script>.
 *
 * La versión anterior leía `document.currentScript.dataset.api`, y eso **no
 * funciona nunca aquí**: el paquete se compila como módulo ES —tiene un
 * `import` de verdad en la primera línea— y dentro de un módulo
 * `document.currentScript` vale SIEMPRE null. Así que el atributo `data-api`
 * que pone el fragmento de WordPress no se leía jamás y todo caía en el valor
 * por defecto. Como el valor por defecto era el bueno, habría funcionado en
 * producción y habría fallado en cuanto alguien apuntara a otro servidor para
 * probar — el peor tipo de fallo: invisible hasta el día que importa.
 *
 * `import.meta.url` sí existe en un módulo y es la URL de este archivo:
 * `https://api.ensal.es/widget/1.0.0/widget.js`. Su origen ES el de la API,
 * porque la propia API sirve el paquete. Un dato menos que configurar y una
 * forma menos de configurarlo mal.
 *
 * Se deja `data-api` como anulación manual por si algún día el paquete se sirve
 * desde otro sitio, pero leído del <script> por id, que en un módulo sí va.
 */
const API =
  document.querySelector<HTMLScriptElement>('script[data-api]')?.dataset.api
  ?? new URL('../..', import.meta.url).origin;

/**
 * Círculo, no píldora, y a la derecha.
 *
 * ENSAL ya tiene su burbuja de WhatsApp abajo a la IZQUIERDA. Dos flotantes
 * distintos en la misma esquina se tapan, así que este va a la derecha y con la
 * misma forma y tamaño que el suyo: 56 px de diámetro. Que los dos se parezcan
 * no es capricho estético — el visitante los lee como dos formas de hablar con
 * la academia, no como un elemento del sitio y un añadido.
 *
 * Antes era una píldora con la etiqueta «Resuelve tus dudas». Se quita el texto
 * por decisión de ENSAL, pero NO se quita del `aria-label`: quien navega con
 * lector de pantalla sigue oyendo qué es y que es una IA, que además es lo que
 * exige el artículo 50 del reglamento europeo de IA. Un icono sin nombre
 * accesible es un botón que no se puede usar.
 *
 * El tamaño es una variable para poder ajustarlo en un minuto cuando midamos la
 * burbuja de WhatsApp en el sitio real, sin recompilar mentalmente medio
 * archivo.
 */
const CSS = `
:host{all:initial;position:fixed;right:20px;bottom:20px;z-index:2147483000;
  --lado:56px;
  font-family:system-ui,-apple-system,"Segoe UI",sans-serif}
@media(max-width:600px){:host{right:14px;bottom:14px;--lado:52px}}
.b{display:flex;align-items:center;justify-content:center;
  width:var(--lado);height:var(--lado);padding:0;
  border:0;border-radius:50%;background:#7C1F35;color:#fff;cursor:pointer;
  box-shadow:0 4px 16px rgba(0,0,0,.22);
  transition:transform .18s ease,box-shadow .18s ease}
.b:hover{transform:translateY(-1px);box-shadow:0 6px 22px rgba(0,0,0,.28)}
.b:focus-visible{outline:3px solid #7C1F35;outline-offset:3px}
.b svg{width:calc(var(--lado) * .5);height:calc(var(--lado) * .5);flex:none}
@media(prefers-reduced-motion:reduce){.b{transition:none}}
`;

class EnsalChat extends HTMLElement {
  #cargando = false;

  connectedCallback() {
    const root = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = CSS;

    const boton = document.createElement('button');
    boton.className = 'b';
    boton.type = 'button';
    // Etiqueta de IA visible desde el primer momento (AI Act art. 50).
    boton.setAttribute('aria-label', 'Abrir asistente virtual con IA de ENSAL');
    boton.innerHTML =
      `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
         <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.1
                  A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z"/>
       </svg>`;

    // Precalentar el chunk en cuanto haya intención, sin llegar a ejecutarlo.
    // Ahorra ~200 ms percibidos y no cuesta nada al que nunca se acerca.
    const precalentar = () => {
      import('./engine');
      boton.removeEventListener('pointerenter', precalentar);
      boton.removeEventListener('focus', precalentar);
    };
    boton.addEventListener('pointerenter', precalentar, { once: true, passive: true });
    boton.addEventListener('focus', precalentar, { once: true });

    boton.addEventListener('click', () => this.#abrir(root, boton));

    root.append(style, boton);
  }

  async #abrir(root: ShadowRoot, boton: HTMLButtonElement) {
    if (this.#cargando) return;
    this.#cargando = true;
    boton.disabled = true;
    try {
      const { montarChat } = await import('./engine');
      await montarChat(root, { api: API, cerrar: () => { boton.disabled = false; } });
      boton.style.display = 'none';
    } catch {
      // Si el motor no carga, el usuario no se queda con un botón muerto.
      boton.disabled = false;
      window.location.href = 'https://www.ensal.es/contacto/';
    } finally {
      this.#cargando = false;
    }
  }
}

/**
 * Registro diferido: no competimos con el LCP de la página. El widget aparece
 * cuando el navegador está ocioso o el usuario empieza a moverse por la página,
 * lo que ocurra antes.
 */
function registrar() {
  if (customElements.get('ensal-chat')) return;
  customElements.define('ensal-chat', EnsalChat);
  document.body.appendChild(document.createElement('ensal-chat'));
}

const arrancar = () => {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(registrar, { timeout: 3500 });
  } else {
    setTimeout(registrar, 2200);
  }
};

if (document.readyState === 'complete') arrancar();
else window.addEventListener('load', arrancar, { once: true });
